import React, { useState, useEffect } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const TotalHubSales = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalSales, setTotalSales] = useState(0);
  const [salesPerProduct, setSalesPerProduct] = useState([]);
  const [salesPerUser, setSalesPerUser] = useState([]);

  const [viewType, setViewType] = useState("product");
  const [chartType, setChartType] = useState("pie");

  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const orgId = localStorage.getItem("orgId") || "";
    const url = `/api/totalHubSalesAgriPromoter/?orgId=${orgId}`;

    fetchData({ url }, (res) => {
      setTotalSales(res?.totalSales || 0);
      setSalesPerProduct(res?.salesPerProduct || []);
      setSalesPerUser(res?.salesPerUser || []);
      setLoading(false);
    });
  }, [fetchData]);

  let chartDataset = [];

  if (viewType === "product") {
    chartDataset =
      selectedProduct === "all"
        ? salesPerProduct
        : salesPerProduct.filter((p) => p.productName === selectedProduct);
  } else {
    chartDataset =
      selectedUser === "all"
        ? salesPerUser
        : salesPerUser.filter(
            (u) => String(u.userId) === String(selectedUser)
          );
  }

  let displayValue = totalSales;

  if (viewType === "product" && selectedProduct !== "all") {
    const rows = salesPerProduct.filter(
      (p) => p.productName === selectedProduct
    );
    displayValue = rows.reduce(
      (sum, r) => sum + (r.totalSales || 0),
      0
    );
  }

  if (viewType === "user" && selectedUser !== "all") {
    const row = salesPerUser.find(
      (u) => String(u.userId) === String(selectedUser)
    );
    displayValue = row ? row.totalSales || 0 : 0;
  }

  const noData =
    !loading && !salesPerProduct.length && !salesPerUser.length;

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: "20px",
          background: "#f1f7ff",
          borderRadius: "14px",
          boxSizing: "border-box",
        }}
      >
        <div
          className="flex justify-between items-center mb-5"
          style={{ gap: 12, flexWrap: "wrap" }}
        >
          <Select
            value={viewType}
            style={{ width: 180 }}
            onChange={(v) => {
              setViewType(v);
              setSelectedProduct("all");
              setSelectedUser("all");
            }}
          >
            <Option value="product">Product Wise</Option>
            <Option value="user">User Wise</Option>
          </Select>

          {viewType === "product" && (
            <Select
              value={selectedProduct}
              style={{ width: 220, marginLeft: 5, marginRight: 5 }}
              onChange={(v) => setSelectedProduct(v)}
            >
              <Option value="all">All Products</Option>
              {salesPerProduct.map((p, i) => (
                <Option key={i} value={p.productName || "Unknown"}>
                  {p.productName || "Unknown Product"}
                </Option>
              ))}
            </Select>
          )}

          {viewType === "user" && (
            <Select
              value={selectedUser}
              style={{ width: 240, marginLeft: 5, marginRight: 5 }}
              onChange={(v) => setSelectedUser(v)}
            >
              <Option value="all">All Users</Option>
              {salesPerUser.map((u, i) => (
                <Option key={i} value={u.userId}>
                  {u.userName || `User ${u.userId}`}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 160, marginLeft: "auto" }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        {loading ? (
          <div
            style={{
              height: "calc(100% - 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        ) : noData ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#666",
            }}
          >
            <h3 style={{ marginBottom: 4 }}>No hub sales data</h3>
            <p style={{ margin: 0 }}>Try changing filters or date range.</p>
          </div>
        ) : (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: "16px",
                padding: "12px",
                background: "#e0ecff",
                borderRadius: "12px",
              }}
            >
              <h1
                style={{
                  fontSize: "32px",
                  fontWeight: "700",
                  color: "#123e73",
                  marginBottom: 4,
                }}
              >
                ₹ {Number(displayValue || 0).toLocaleString()}
              </h1>

              <p
                style={{
                  fontSize: "16px",
                  color: "#123e73",
                  margin: 0,
                }}
              >
                {viewType === "product"
                  ? selectedProduct === "all"
                    ? "Total Hub Sales (All Products)"
                    : `Sales — ${selectedProduct}`
                  : selectedUser === "all"
                    ? "Total Hub Sales (All Users)"
                    : `Sales — ${
                        salesPerUser.find(
                          (u) => String(u.userId) === String(selectedUser)
                        )?.userName || "Selected User"
                      }`}
              </p>
            </div>

            <div style={{ height: "100%", overflow: "hidden" }}>
              {chartType === "pie" && (
                <DynamicPieChart
                  data={chartDataset}
                  angleField="totalSales"
                  colorField={
                    viewType === "product" ? "productName" : "userName"
                  }
                  isMaximized={true}
                />
              )}

              {chartType === "column" && (
                <DynamicColumnChart
                  data={chartDataset}
                  xField={
                    viewType === "product" ? "productName" : "userName"
                  }
                  yField="totalSales"
                  seriesField={
                    viewType === "product" ? "productName" : "userName"
                  }
                  xAxisTitle={
                    viewType === "product" ? "Product" : "User"
                  }
                  yAxisTitle="Sales Amount"
                />
              )}

              {chartType === "bar" && (
                <DynamicBarChart
                  data={chartDataset}
                  xField={
                    viewType === "product" ? "productName" : "userName"
                  }
                  yField="totalSales"
                  seriesField={
                    viewType === "product" ? "productName" : "userName"
                  }
                  xAxisTitle={
                    viewType === "product" ? "Product" : "User"
                  }
                  yAxisTitle="Sales Amount"
                />
              )}

              {chartType === "line" && (
                <DynamicLineChart
                  data={chartDataset}
                  xField={
                    viewType === "product" ? "productName" : "userName"
                  }
                  yField="totalSales"
                  seriesField={
                    viewType === "product" ? "productName" : "userName"
                  }
                  xAxisTitle={
                    viewType === "product" ? "Product" : "User"
                  }
                  yAxisTitle="Sales Amount"
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: "200px",
        width: "100%",
        padding: "18px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : noData ? (
        <div
          className="flex justify-center items-center h-full"
          style={{ color: "#3d5c26" }}
        >
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: 0 }}>No Hub Sales</h3>
            <p style={{ marginTop: 4, fontSize: 13 }}>No sales recorded</p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontWeight: "700",
                color: "#3d5c26",
                marginBottom: 4,
              }}
            >
              {Number(totalSales || 0).toLocaleString()}
            </h1>
            <h3
              style={{
                fontWeight: 700,
                margin: 0,
                color: "#3d5c26",
              }}
            >
              Total Hub Sales
            </h3>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "8px",
              right: "18px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#3d5c26",
              fontSize: "14px",
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default TotalHubSales;
